import React from 'react';
import { X } from 'lucide-react';

interface BVNInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const BVNInfoModal: React.FC<BVNInfoModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-1">
                    <h2 className="text-xl font-bold text-gray-900">About BVN Verification</h2>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500 flex-shrink-0 ml-3"
                        aria-label="Close"
                        type="button"
                    >
                        <X size={16} />
                    </button>
                </div>
                <p className="text-sm text-gray-500 mb-5">
                    We use your BVN only for identity verification, fraud prevention, and secure withdrawals.
                </p>

                <div className="bg-gray-50 rounded-2xl p-5 space-y-5">
                    <div>
                        <h3 className="font-bold text-gray-900 text-sm mb-1.5">1. What is BVN?</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            The Bank Verification Number (BVN) is a unique identifier issued by Nigerian banks to
                            verify your identity. It helps financial institutions confirm that account holders are
                            who they claim to be.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-bold text-gray-900 text-sm mb-1.5">2. Why we need your BVN?</h3>
                        <p className="text-sm text-gray-600 mb-2">We use your BVN to:</p>
                        <ul className="text-sm text-gray-600 space-y-1 mb-2 list-disc pl-5">
                            <li>Verify your identity</li>
                            <li>Prevent fraud and duplicate accounts</li>
                            <li>Comply with Know Your Customer (KYC) requirements</li>
                            <li>Enable secure reward withdrawals</li>
                        </ul>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Your BVN helps us keep the VouchLinks platform safe and trustworthy for all users.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-bold text-gray-900 text-sm mb-1.5">3. How does BVN verification work?</h3>
                        <p className="text-sm text-gray-600 leading-relaxed mb-2">
                            To verify your identity, we securely send the details you enter to our trusted
                            verification partner. They compare the information against the official BVN records and
                            return a verification result.
                        </p>
                        <p className="text-sm text-gray-600 leading-relaxed mb-2">
                            We only use this process to confirm your identity and enable secure withdrawals.
                        </p>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            It's encrypted, never stored in plain text and used only for verification, not for
                            accessing your bank account.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BVNInfoModal;
